import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/brand/Logo";
import { Stat } from "@/components/admin/common";
import { Field } from "@/components/editor/fields";
import { QrDesigner } from "@/components/qr/QrStudio";
import { api, formatErr } from "@/lib/api";

export default function PortalQr() {
  const { id } = useParams();
  const [it, setIt] = useState(null);
  const [target, setTarget] = useState("");
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    api.get(`/qr-links/${id}`).then((r) => { setIt(r.data); setTarget(r.data.target_url); }).catch(() => setMissing(true));
  }, [id]);
  if (missing) return <div className="p-10 text-sm" data-testid="portal-qr-missing">QR non trovato. <Link to="/portale" className="underline">Torna alla tua area</Link></div>;
  if (!it) return <div className="p-10 eyebrow">Caricamento…</div>;
  const dynamic = it.mode === "dynamic";
  const save = async () => {
    try {
      const { data } = await api.put(`/qr-links/${id}`, { name: it.name, target_url: target, client_id: it.client_id || "", mode: it.mode, active: it.active });
      setIt(data);
      setTarget(data.target_url);
      toast.success("Salvato: il QR ora porta al nuovo link");
    } catch (e) {
      toast.error(formatErr(e));
    }
  };
  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      <header className="border-b border-black/[0.06] bg-white/80 px-5 py-4 backdrop-blur-xl sm:px-10"><Logo size={28} /></header>
      <main className="mx-auto max-w-5xl px-5 py-10 sm:px-8">
        <Link to="/portale" className="mb-6 inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900" data-testid="portal-qr-back"><ArrowLeft className="h-4 w-4" />La tua area</Link>
        <div className="eyebrow">{dynamic ? "QR dinamico" : "QR diretto"}</div>
        <h1 className="mt-1 font-display text-4xl sm:text-5xl" data-testid="portal-qr-title">{it.name}</h1>
        <div className="mb-8 mt-8 grid gap-6 lg:grid-cols-[1fr_320px]">
          <div className="panel space-y-4 p-6">
            {dynamic ? (
              <>
                <Field label="Link di destinazione" value={target} onChange={setTarget} testId="portal-qr-target" hint="Puoi cambiarlo quando vuoi: i QR già stampati porteranno al nuovo link." />
                <Button onClick={save} disabled={!target || target === it.target_url} data-testid="portal-qr-save">Salva</Button>
              </>
            ) : (
              <div className="text-sm text-zinc-600" data-testid="portal-qr-static">Questo QR contiene direttamente il link <span className="break-all font-mono-ui text-zinc-900">{it.target_url}</span> e non può essere modificato. Per cambiarlo contatta chi gestisce il tuo account.</div>
            )}
            {!it.active && <div className="text-sm text-amber-700" data-testid="portal-qr-inactive">Questo QR è al momento disattivato.</div>}
          </div>
          {dynamic && <div className="grid gap-3"><Stat label="Scansioni totali" value={it.scans} testId="portal-qr-scans" /><Stat label="Ultimi 30 giorni" value={it.scans_30d} testId="portal-qr-scans-30" /></div>}
        </div>
        <QrDesigner initial={it.qr} getTarget={() => (dynamic ? it.short_url : it.target_url)} shareUrl={dynamic ? it.short_url : it.target_url}
          fileBase={`qr-${it.code}`} saveUrl={`/qr-links/${id}/qr`} note="Scarica il QR in PNG per il web o in SVG per la stampa." />
      </main>
    </div>
  );
}
